import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  ConfirmActionModal,
  EmptyHubState,
  EntityFormDialog,
  BulkImportDialog,
  MiniLoader,
  StatusChip,
  type FieldSection,
  type ImportColumnDef,
} from '../Common'
import { JoinCodesOncePanel, type JoinCodeOnceItem } from '../JoinCodes/JoinCodesOncePanel'
import { chunkArray } from '../JoinCodes/joinCodeUtils'
import { useNls } from '../../nls/nlsProvider'
import { useWorkspace } from '../../context/useWorkspace'
import { useAuth } from '../../context/useAuth'
import { isWorkspaceOwner } from '../../utils/workspaceOwners'
import { formatRoleLabel, roleLabelKey } from '../../utils/roleLabels'
import { getWhatsappInviteInfo } from '../../services/whatsappService'
import {
  createJoinCodes,
  listJoinCodes,
  reissueJoinCode,
  revokeJoinCode,
  type CreateJoinCodeRow,
  type JoinCodeDoc,
  type JoinCodeRole,
  type JoinCodeStatus,
} from '../../services/joinCodeService'
import type { WorkspaceRole } from '../../types/workspace'
import './joinCodesPanel.css'

interface ServerErrorLike {
  message?: string
  code?: string
  details?: {
    code?: string
    used?: number
    seats?: number
  }
}

export const JoinCodesPanel: React.FC = () => {
  const { t } = useNls()
  const { activeWorkspaceId, workspaces, activeRole } = useWorkspace()
  const activeWorkspace = workspaces.find((entry) => entry.id === activeWorkspaceId)
  const { currentUser } = useAuth()
  const isOwner = isWorkspaceOwner(activeWorkspace, currentUser?.uid)
  const isAdmin = isOwner || activeRole === 'ADMIN' || activeRole === 'OWNER'
  const [whatsappNumberE164, setWhatsappNumberE164] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    if (activeWorkspaceId && isAdmin) {
      getWhatsappInviteInfo(activeWorkspaceId)
        .then((info) => {
          if (isMounted) setWhatsappNumberE164(info.sendingNumberE164)
        })
        .catch(() => {
          if (isMounted) setWhatsappNumberE164(null)
        })
    }
    return () => {
      isMounted = false
      setWhatsappNumberE164(null)
    }
  }, [activeWorkspaceId, isAdmin])

  const [loading, setLoading] = useState(true)
  const [codes, setCodes] = useState<JoinCodeDoc[]>([])
  const [createdCodes, setCreatedCodes] = useState<JoinCodeOnceItem[]>([])
  const [error, setError] = useState<string | null>(null)

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [isImportOpen, setIsImportOpen] = useState(false)

  const [codeToRevoke, setCodeToRevoke] = useState<JoinCodeDoc | null>(null)
  const [isRevoking, setIsRevoking] = useState(false)
  const [refreshIndex, setRefreshIndex] = useState(0)

  useEffect(() => {
    let isMounted = true
    void (async () => {
      if (!activeWorkspaceId) {
        setCodes([])
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const docs = await listJoinCodes(activeWorkspaceId)
        if (!isMounted) return
        const staffCodes = docs.filter((d) => d.prepared?.role !== 'LEARNER')
        setCodes(staffCodes)
      } catch (err) {
        if (!isMounted) return
        console.warn('[JoinCodesPanel] list error', err)
        setError(t('NLSJoinCodesLoadError'))
      } finally {
        if (isMounted) setLoading(false)
      }
    })()

    return () => {
      isMounted = false
    }
  }, [activeWorkspaceId, refreshIndex, t])

  const formatServerError = (err: unknown): string => {
    const errorObj = err as ServerErrorLike
    const code = errorObj?.details?.code || errorObj?.code
    if (
      code === 'SEATS_FULL' ||
      errorObj?.message?.includes('SEATS_FULL') ||
      errorObj?.message?.includes('seat limit reached')
    ) {
      const used = errorObj?.details?.used ?? '?'
      const seats = errorObj?.details?.seats ?? '?'
      return t('NLSJoinCodesSeatsFull', { used, seats })
    }
    if (code === 'NOT_REISSUABLE' || errorObj?.message?.includes('NOT_REISSUABLE')) {
      return t('NLSJoinCodesNotReissuable')
    }
    if (code === 'ALREADY_USED' || errorObj?.message?.includes('ALREADY_USED')) {
      return t('NLSJoinCodesAlreadyUsed')
    }
    return errorObj?.message || t('NLSJoinCodesGenericError')
  }

  const roleOptions: Array<{ label: string; value: string }> = useMemo(() => {
    const opts: Array<{ label: string; value: string }> = []
    if (isOwner) {
      opts.push({ label: t(roleLabelKey('ADMIN')), value: 'ADMIN' })
    }
    opts.push(
      { label: t(roleLabelKey('EDITOR')), value: 'EDITOR' },
      { label: t(roleLabelKey('VIEWER')), value: 'VIEWER' },
      { label: t(roleLabelKey('HELPER')), value: 'HELPER' },
    )
    return opts
  }, [isOwner, t])

  const createSections: FieldSection[] = useMemo(
    () => [
      {
        id: 'main',
        label: t('NLSJoinCodesCreateDialogTitle'),
        fields: [
          {
            id: 'name',
            label: t('NLSJoinCodesColName'),
            type: 'text',
            required: true,
            validate: (val: unknown) => {
              if (!val || typeof val !== 'string' || !val.trim()) {
                return t('NLSJoinCodesNameRequired')
              }
              return null
            },
          },
          {
            id: 'role',
            label: t('NLSJoinCodesColRole'),
            type: 'select',
            required: true,
            options: roleOptions,
            defaultValue: 'VIEWER',
          },
        ],
      },
    ],
    [roleOptions, t],
  )

  const handleCreateOne = async (values: Record<string, unknown>) => {
    setCreateError(null)
    const name = String(values.name ?? '').trim()
    const role = String(values.role ?? 'VIEWER').toUpperCase() as JoinCodeRole

    if (role === 'ADMIN' && !isOwner) {
      const errText = t('NLSJoinCodesAdminOwnerOnly')
      setCreateError(errText)
      setError(errText)
      throw new Error(errText)
    }

    try {
      const res = await createJoinCodes({
        workspaceId: activeWorkspaceId,
        rows: [{ displayName: name, role }],
      })
      setIsCreateOpen(false)
      if (res.codes && res.codes.length > 0) {
        const newItems: JoinCodeOnceItem[] = res.codes.map((c) => ({
          key: c.id,
          displayName: c.displayName,
          subtitle: formatRoleLabel(t, c.role),
          code: c.code,
          pin: c.pin,
          expiresAtMs: c.expiresAtMs,
        }))
        setCreatedCodes((prev) => [...prev, ...newItems])
      }
      setRefreshIndex((i) => i + 1)
    } catch (err: unknown) {
      const msg = formatServerError(err)
      setCreateError(msg)
      const handled = err instanceof Error ? err : new Error(String(err))
      ;(handled as unknown as { isHandled?: boolean }).isHandled = true
      throw handled
    }
  }

  const importColumns: ImportColumnDef[] = useMemo(
    () => [
      {
        id: 'name',
        label: t('NLSJoinCodesColName'),
        type: 'string',
        required: true,
        validate: (val: unknown) => {
          if (!val || typeof val !== 'string' || !val.trim()) {
            return t('NLSJoinCodesNameRequired')
          }
          return null
        },
      },
      {
        id: 'role',
        label: t('NLSJoinCodesColRole'),
        type: 'string',
        required: true,
        validate: (val: unknown) => {
          if (!val || typeof val !== 'string' || !val.trim()) {
            return t('NLSJoinCodesRoleRequired')
          }
          const norm = val.trim().toUpperCase()
          if (
            norm !== 'ADMIN' &&
            norm !== 'EDITOR' &&
            norm !== 'VIEWER' &&
            norm !== 'HELPER'
          ) {
            return t('NLSJoinCodesInvalidRole')
          }
          if (norm === 'ADMIN' && !isOwner) {
            return t('NLSJoinCodesAdminOwnerOnly')
          }
          return null
        },
      },
    ],
    [isOwner, t],
  )

  const handleBulkCommit = async (
    validRows: Array<Record<string, unknown>>,
    onProgress: (committed: number, total: number) => void,
  ) => {
    setError(null)
    const mappedRows: CreateJoinCodeRow[] = validRows.map((r) => ({
      displayName: String(r.name ?? '').trim(),
      role: String(r.role ?? '').trim().toUpperCase() as JoinCodeRole,
    }))

    const chunks = chunkArray(mappedRows, 500)
    let committed = 0

    for (const chunk of chunks) {
      try {
        const res = await createJoinCodes({
          workspaceId: activeWorkspaceId,
          rows: chunk,
        })
        committed += chunk.length
        onProgress(committed, mappedRows.length)
        if (res.codes && res.codes.length > 0) {
          const newItems: JoinCodeOnceItem[] = res.codes.map((c) => ({
            key: c.id,
            displayName: c.displayName,
            subtitle: formatRoleLabel(t, c.role),
            code: c.code,
            pin: c.pin,
            expiresAtMs: c.expiresAtMs,
          }))
          setCreatedCodes((prev) => [...prev, ...newItems])
        }
      } catch (err: unknown) {
        setRefreshIndex((i) => i + 1)
        if (committed > 0) {
          const partialMsg = t('NLSJoinCodesPartialCommitError', {
            created: committed,
            total: mappedRows.length,
          })
          setError(partialMsg)
        } else {
          setError(formatServerError(err))
        }
        setIsImportOpen(false)
        const handled = err instanceof Error ? err : new Error(String(err))
        ;(handled as unknown as { isHandled?: boolean }).isHandled = true
        throw handled
      }
    }

    setRefreshIndex((i) => i + 1)
  }

  const handleReissueCode = async (codeId: string) => {
    setError(null)
    try {
      const res = await reissueJoinCode({ joinCodeId: codeId })
      const newItem: JoinCodeOnceItem = {
        key: res.id,
        displayName: res.displayName,
        subtitle: formatRoleLabel(t, res.role),
        code: res.code,
        pin: res.pin,
        expiresAtMs: res.expiresAtMs,
      }
      setCreatedCodes((prev) => [...prev, newItem])
      setRefreshIndex((i) => i + 1)
    } catch (err) {
      setError(formatServerError(err))
    }
  }

  const handleRevokeConfirm = async () => {
    if (!codeToRevoke) return
    setIsRevoking(true)
    setError(null)
    try {
      await revokeJoinCode({ joinCodeId: codeToRevoke.id })
      setCodeToRevoke(null)
      setRefreshIndex((i) => i + 1)
    } catch (err) {
      setCodeToRevoke(null)
      setError(formatServerError(err))
    } finally {
      setIsRevoking(false)
    }
  }

  const formatCodeExpiry = (expiresAt: JoinCodeDoc['expiresAt']): string => {
    if (!expiresAt) return '—'
    let ms = NaN
    if (typeof expiresAt === 'object' && expiresAt !== null) {
      if ('toDate' in expiresAt && typeof (expiresAt as { toDate: () => Date }).toDate === 'function') {
        ms = (expiresAt as { toDate: () => Date }).toDate().getTime()
      } else if ('seconds' in expiresAt && typeof (expiresAt as { seconds: number }).seconds === 'number') {
        ms = (expiresAt as { seconds: number }).seconds * 1000
      }
    } else if (typeof expiresAt === 'number') {
      ms = expiresAt
    }
    if (isNaN(ms)) return '—'
    return new Date(ms).toLocaleDateString()
  }

  const getStatusChipTone = (status: JoinCodeStatus): 'info' | 'success' | 'danger' | 'neutral' => {
    switch (status) {
      case 'PENDING':
        return 'info'
      case 'CLAIMED':
        return 'success'
      case 'LOCKED':
      case 'REVOKED':
        return 'danger'
      case 'EXPIRED':
      default:
        return 'neutral'
    }
  }

  const getStatusLabel = (status: JoinCodeStatus): string => {
    switch (status) {
      case 'PENDING':
        return t('NLSJoinCodesStatusPending')
      case 'CLAIMED':
        return t('NLSJoinCodesStatusClaimed')
      case 'EXPIRED':
        return t('NLSJoinCodesStatusExpired')
      case 'REVOKED':
        return t('NLSJoinCodesStatusRevoked')
      case 'LOCKED':
        return t('NLSJoinCodesStatusLocked')
      default:
        return status
    }
  }

  return (
    <section className="joinCodesPanel" data-testid="join-codes-panel">
      <header className="joinCodesPanel__header">
        <div className="joinCodesPanel__titles">
          <h2 className="joinCodesPanel__title">{t('NLSJoinCodesPanelTitle')}</h2>
          <p className="joinCodesPanel__desc">{t('NLSJoinCodesPanelDescription')}</p>
        </div>
        <div className="joinCodesPanel__actions">
          <Button
            variant="primary"
            size="sm"
            data-testid="join-codes-create-btn"
            onClick={() => {
              setCreateError(null)
              setIsCreateOpen(true)
            }}
          >
            {t('NLSJoinCodesCreateOneBtn')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            data-testid="join-codes-import-btn"
            onClick={() => {
              setError(null)
              setIsImportOpen(true)
            }}
          >
            {t('NLSJoinCodesImportCsvBtn')}
          </Button>
        </div>
      </header>

      {error && (
        <div className="joinCodesPanel__error" role="alert" data-testid="join-codes-error">
          {error}
        </div>
      )}

      {createdCodes.length > 0 && (
        <JoinCodesOncePanel
          items={createdCodes}
          workspaceName={activeWorkspace?.name || ''}
          whatsappNumberE164={whatsappNumberE164}
          onClear={() => setCreatedCodes([])}
        />
      )}

      {loading ? (
        <MiniLoader />
      ) : codes.length === 0 ? (
        <EmptyHubState
          title={t('NLSJoinCodesEmptyTitle')}
          description={t('NLSJoinCodesEmptyBody')}
        />
      ) : (
        <div className="joinCodesPanel__tableWrapper">
          <table className="joinCodesPanel__table">
            <thead>
              <tr>
                <th>{t('NLSJoinCodesColName')}</th>
                <th>{t('NLSJoinCodesColRole')}</th>
                <th>{t('NLSJoinCodesColStatus')}</th>
                <th>{t('NLSJoinCodesColCode')}</th>
                <th>{t('NLSJoinCodesColExpiry')}</th>
                <th>{t('NLSJoinCodesColActions')}</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const canReissue = c.status !== 'CLAIMED' && c.status !== 'REVOKED'
                const canRevoke = c.status === 'PENDING' || c.status === 'LOCKED'
                return (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.prepared?.displayName || '—'}</strong>
                    </td>
                    <td>{formatRoleLabel(t, c.prepared?.role as WorkspaceRole)}</td>
                    <td>
                      <StatusChip
                        tone={getStatusChipTone(c.status)}
                        size="sm"
                      >
                        {getStatusLabel(c.status)}
                      </StatusChip>
                    </td>
                    <td>
                      <span className="joinCodesPanel__codeHint">••{c.codeHint}</span>
                    </td>
                    <td>{formatCodeExpiry(c.expiresAt)}</td>
                    <td>
                      <div className="joinCodesPanel__rowActions">
                        {canReissue && (
                          <Button
                            variant="secondary"
                            size="sm"
                            data-testid={`reissue-btn-${c.id}`}
                            onClick={() => void handleReissueCode(c.id)}
                          >
                            {t('NLSJoinCodesReissue')}
                          </Button>
                        )}
                        {canRevoke && (
                          <Button
                            variant="secondary"
                            size="sm"
                            data-testid={`revoke-btn-${c.id}`}
                            onClick={() => setCodeToRevoke(c)}
                          >
                            {t('NLSJoinCodesRevoke')}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {isCreateOpen && (
        <div data-testid="join-codes-create-dialog">
          <EntityFormDialog
            open={isCreateOpen}
            entityKind="joinCode"
            entityName="Join code"
            sections={createSections}
            onClose={() => {
              setIsCreateOpen(false)
              setCreateError(null)
            }}
            onSave={handleCreateOne}
            validateAll={() => {
              const errs: Record<string, string> = {}
              if (createError) {
                errs.name = createError
              }
              return errs
            }}
          />
        </div>
      )}

      {isImportOpen && (
        <div data-testid="join-codes-import-dialog">
          <BulkImportDialog
            open={isImportOpen}
            title={t('NLSJoinCodesImportDialogTitle')}
            entityName="Join codes"
            columns={importColumns}
            onClose={() => setIsImportOpen(false)}
            onCommit={handleBulkCommit}
          />
        </div>
      )}

      {codeToRevoke && (
        <ConfirmActionModal
          open={Boolean(codeToRevoke)}
          title={t('NLSJoinCodesRevokeConfirmTitle')}
          body={t('NLSJoinCodesRevokeConfirmBody')}
          confirmLabel={t('NLSJoinCodesRevokeConfirmAction')}
          danger
          busy={isRevoking}
          onClose={() => setCodeToRevoke(null)}
          onConfirm={handleRevokeConfirm}
        />
      )}
    </section>
  )
}
